import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import {
  ArrowRight, ArrowDown, Sparkles, Target, RotateCcw, AlertTriangle,
  History, EyeOff, Brain, LineChart, Layers, FileText, PenTool, Grid3x3,
  Newspaper, GraduationCap, BookOpen, Scale, Compass, Trophy, Check,
  TrendingUp, Activity, Gauge, ChevronRight, Clock, CreditCard, Quote, Users,
} from "lucide-react";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";
import SEO from "@/components/SEO";

/* ---------------------------------------------------------------- primitives */
const DISPLAY = "font-['Outfit']";
const BODY = "font-['Inter']";

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 22 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

const Section = ({ id, bg, children, className = "" }) => (
  <section id={id} style={{ backgroundColor: bg }} className={`w-full ${className}`}>
    <div className="max-w-7xl mx-auto px-6 md:px-8 py-24 md:py-32">{children}</div>
  </section>
);

const Overline = ({ children }) => (
  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#f97316] mb-4">{children}</p>
);

const Bar = ({ label, value, tone = "#f97316" }) => (
  <div className="mb-2.5 last:mb-0">
    <div className="flex justify-between text-[11px] text-slate-400 mb-1">
      <span>{label}</span><span>{value}%</span>
    </div>
    <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
      <motion.div className="h-full rounded-full" style={{ background: tone }}
        initial={{ width: 0 }} whileInView={{ width: `${value}%` }}
        viewport={{ once: true }} transition={{ duration: 1, ease: "easeOut" }} />
    </div>
  </div>
);

const Ring = ({ value, label }) => {
  const r = 34, c = 2 * Math.PI * r;
  return (
    <div className="relative w-[92px] h-[92px]">
      <svg viewBox="0 0 80 80" className="w-full h-full -rotate-90">
        <circle cx="40" cy="40" r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="7" />
        <motion.circle cx="40" cy="40" r={r} fill="none" stroke="#f97316" strokeWidth="7"
          strokeLinecap="round" strokeDasharray={c}
          initial={{ strokeDashoffset: c }} whileInView={{ strokeDashoffset: c - (c * value) / 100 }}
          viewport={{ once: true }} transition={{ duration: 1.2, ease: "easeOut" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`${DISPLAY} text-2xl font-semibold text-white`}>{value}</span>
        <span className="text-[9px] uppercase tracking-wide text-slate-500">{label}</span>
      </div>
    </div>
  );
};

const Panel = ({ children }) => (
  <div className="rounded-xl border border-[rgba(255,255,255,0.08)] bg-[#0B1020] p-4 h-full">{children}</div>
);

/* ------------------------------------------------------------- hero dashboard */
const HeroDashboard = () => (
  <div className="rounded-2xl border border-[rgba(255,255,255,0.1)] bg-[#1A2233] p-5 shadow-2xl shadow-black/40">
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2 text-slate-300 text-sm font-medium">
        <Gauge className="w-4 h-4 text-[#f97316]" /> Decision Dashboard
      </div>
      <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 flex items-center gap-1">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Live
      </span>
    </div>
    <div className="grid grid-cols-3 gap-3">
      <div className="col-span-1 rounded-xl border border-[rgba(255,255,255,0.08)] bg-[#0B1020] p-3 flex flex-col items-center justify-center">
        <Ring value={72} label="Readiness" />
      </div>
      <div className="col-span-2 grid grid-cols-2 gap-3">
        <div className="rounded-xl border border-[rgba(255,255,255,0.08)] bg-[#0B1020] p-3">
          <p className="text-[10px] uppercase tracking-wide text-slate-500">Est. Marks Lost</p>
          <p className={`${DISPLAY} text-2xl font-semibold text-white mt-1`}>-34</p>
          <p className="text-[10px] text-rose-400 flex items-center gap-1 mt-1"><TrendingUp className="w-3 h-3 rotate-180" /> avoidable</p>
        </div>
        <div className="rounded-xl border border-[rgba(255,255,255,0.08)] bg-[#0B1020] p-3">
          <p className="text-[10px] uppercase tracking-wide text-slate-500">Attempt Quality</p>
          <p className={`${DISPLAY} text-2xl font-semibold text-white mt-1`}>B+</p>
          <p className="text-[10px] text-emerald-400 flex items-center gap-1 mt-1"><TrendingUp className="w-3 h-3" /> improving</p>
        </div>
      </div>
    </div>
    <div className="mt-3 rounded-xl border border-[rgba(255,255,255,0.08)] bg-[#0B1020] p-3">
      <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-2">Weak Areas</p>
      <Bar label="Polity — Federalism" value={38} tone="#ef4444" />
      <Bar label="Economy — External Sector" value={52} tone="#f59e0b" />
      <Bar label="Ethics — Case Studies" value={64} tone="#f97316" />
    </div>
  </div>
);

/* --------------------------------------------------------------- product panels */
const AnswerPanel = () => (
  <Panel>
    <div className="flex items-center gap-2 text-slate-300 text-xs font-medium mb-3"><PenTool className="w-4 h-4 text-[#f97316]" /> Topper Answer Generator</div>
    <div className="flex gap-1.5 mb-3 flex-wrap">
      {["GS1", "GS2", "GS3", "GS4", "Essay"].map((p, i) => (
        <span key={p} className={`text-[10px] px-2 py-1 rounded-md ${i === 1 ? "bg-[#f97316] text-white" : "bg-white/[0.06] text-slate-400"}`}>{p}</span>
      ))}
    </div>
    <div className="rounded-lg bg-white/[0.04] p-2.5 text-[11px] text-slate-400 mb-2">"Examine the role of cooperative federalism in Indian polity."</div>
    <div className="space-y-1.5">
      {[["Introduction", "Context + definition"], ["Body", "Multi-dimensional + examples"], ["Value Addition", "Committees · Articles · Data"], ["Conclusion", "Forward-looking"]].map(([t, d]) => (
        <div key={t} className="flex items-center gap-2 text-[11px]">
          <Check className="w-3 h-3 text-emerald-400 shrink-0" />
          <span className="text-slate-200 font-medium">{t}</span>
          <span className="text-slate-500 truncate">— {d}</span>
        </div>
      ))}
    </div>
  </Panel>
);

const PYQPanel = () => (
  <Panel>
    <div className="flex items-center gap-2 text-slate-300 text-xs font-medium mb-3"><Layers className="w-4 h-4 text-[#f97316]" /> PYQ Analyzer</div>
    <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-2">Most-asked topics · 2014–2024</p>
    <Bar label="Fundamental Rights" value={88} />
    <Bar label="Indian Economy — Growth" value={74} />
    <Bar label="Environment — Conventions" value={61} />
    <Bar label="Modern History — Movements" value={49} />
  </Panel>
);

const HeatmapPanel = () => {
  const cells = [90, 20, 60, 80, 35, 15, 70, 45, 95, 30, 55, 25, 85, 40, 65, 10, 75, 50, 20, 88, 33, 60, 42, 78, 15, 92, 48, 28];
  const shade = (v) => `rgba(249,115,22,${(v / 100) * 0.9 + 0.06})`;
  return (
    <Panel>
      <div className="flex items-center gap-2 text-slate-300 text-xs font-medium mb-3"><Grid3x3 className="w-4 h-4 text-[#f97316]" /> Topic Heatmap</div>
      <div className="grid grid-cols-7 gap-1.5">
        {cells.map((v, i) => (
          <motion.div key={i} className="aspect-square rounded-[3px]" style={{ backgroundColor: shade(v) }}
            initial={{ opacity: 0, scale: 0.6 }} whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }} transition={{ duration: 0.3, delay: i * 0.012 }} />
        ))}
      </div>
      <p className="text-[10px] text-slate-500 mt-3">Darker = higher UPSC weightage</p>
    </Panel>
  );
};

const CAPanel = () => (
  <Panel>
    <div className="flex items-center gap-2 text-slate-300 text-xs font-medium mb-3"><Newspaper className="w-4 h-4 text-[#f97316]" /> Current Affairs Intelligence</div>
    <div className="space-y-2">
      {[["Digital Personal Data Protection", "GS2 · Governance", "High"], ["India's Semiconductor Push", "GS3 · Economy", "High"], ["Cooperative Federalism Debate", "GS2 · Polity", "Med"]].map(([t, tag, p]) => (
        <div key={t} className="rounded-lg bg-white/[0.04] p-2.5">
          <p className="text-[11px] text-slate-200 font-medium leading-snug">{t}</p>
          <div className="flex items-center justify-between mt-1">
            <span className="text-[10px] text-slate-500">{tag}</span>
            <span className={`text-[9px] px-1.5 py-0.5 rounded ${p === "High" ? "bg-[#f97316]/20 text-[#f97316]" : "bg-white/[0.06] text-slate-400"}`}>{p} yield</span>
          </div>
        </div>
      ))}
    </div>
  </Panel>
);

const PracticePanel = () => (
  <Panel>
    <div className="flex items-center gap-2 text-slate-300 text-xs font-medium mb-3"><BookOpen className="w-4 h-4 text-[#f97316]" /> Practice Hub</div>
    <div className="space-y-2.5">
      {[["Polity — Union & Territory", 82], ["History — Modern India", 64], ["Geography — Climatology", 45]].map(([t, v]) => (
        <div key={t}>
          <div className="flex justify-between text-[11px] mb-1"><span className="text-slate-200">{t}</span><span className="text-slate-500">{v}%</span></div>
          <Bar label="" value={v} tone={v > 70 ? "#22c55e" : v > 50 ? "#f97316" : "#ef4444"} />
        </div>
      ))}
    </div>
  </Panel>
);

const PerfPanel = () => {
  const pts = [30, 42, 38, 55, 60, 72, 68, 80];
  const w = 220, h = 60, max = 100;
  const path = pts.map((p, i) => `${(i / (pts.length - 1)) * w},${h - (p / max) * h}`).join(" ");
  return (
    <Panel>
      <div className="flex items-center gap-2 text-slate-300 text-xs font-medium mb-3"><Activity className="w-4 h-4 text-[#f97316]" /> Performance Dashboard</div>
      <div className="flex gap-4 mb-3">
        <div><p className="text-[10px] uppercase text-slate-500">Percentile</p><p className={`${DISPLAY} text-xl text-white font-semibold`}>91</p></div>
        <div><p className="text-[10px] uppercase text-slate-500">Trend</p><p className="text-xl text-emerald-400 font-semibold">↑</p></div>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-16">
        <motion.polyline points={path} fill="none" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"
          initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }} transition={{ duration: 1.4 }} />
      </svg>
    </Panel>
  );
};

const DashPanel = () => (
  <Panel><HeroDashboardMini /></Panel>
);
const HeroDashboardMini = () => (
  <div>
    <div className="flex items-center gap-2 text-slate-300 text-xs font-medium mb-3"><Gauge className="w-4 h-4 text-[#f97316]" /> Decision Dashboard</div>
    <div className="flex items-center gap-4">
      <Ring value={72} label="Ready" />
      <div className="flex-1 space-y-2">
        <Bar label="Polity" value={38} tone="#ef4444" />
        <Bar label="Economy" value={52} tone="#f59e0b" />
        <Bar label="Ethics" value={64} tone="#f97316" />
      </div>
    </div>
  </div>
);

/* ============================================================================ */
export default function PublicLandingV7() {
  const navigate = useNavigate();
  const B1 = "#0B1020", B2 = "#111827";
  const startDiagnostic = () => navigate("/free-diagnostic");
  const startFree = () => navigate("/auth");
  const seeDemo = () => document.getElementById("see-in-action")?.scrollIntoView({ behavior: "smooth" });

  const btnPrimary = "inline-flex items-center justify-center gap-2 rounded-lg bg-[#f97316] hover:bg-[#ea580c] text-white px-6 py-3.5 text-sm font-semibold transition-colors";
  const btnGhost = "inline-flex items-center justify-center gap-2 rounded-lg border border-[rgba(255,255,255,0.15)] hover:border-[rgba(255,255,255,0.3)] hover:bg-white/[0.04] text-white px-6 py-3.5 text-sm font-semibold transition-colors";
  const H2 = `${DISPLAY} text-3xl md:text-4xl tracking-tight font-medium text-white`;
  const engines = [
    { icon: PenTool, title: "Topper Answer Intelligence", from: "Question", to: "Selection-Level Answer",
      tags: ["GS1", "GS2", "GS3", "GS4", "Essay"], note: "Different papers demand different thinking — so each has its own engine.", outcome: "Write the way selected candidates write." },
    { icon: Brain, title: "Decision Intelligence", from: "Behaviour", to: "Preparation Insight",
      tags: ["Strategy Analytics", "Weakness Detection", "Decision Dashboard", "Revision Intelligence", "Performance Tracking"], note: "Turn how you study into what you should change.", outcome: "Know where marks disappear — before the exam." },
    { icon: Layers, title: "PYQ Intelligence", from: "Past Papers", to: "Pattern Intelligence",
      tags: ["Topic Heatmaps", "Pattern Recognition", "Frequency Analysis", "Trend Analysis", "Exam Behaviour"], note: "A decade of papers, read as signal not noise.", outcome: "Study what UPSC repeatedly rewards." },
  ];

  return (
    <div className={`${BODY} text-white`} style={{ backgroundColor: B1 }} data-testid="landing-v7">
      <SEO title="CLARIAS — Decision Intelligence for UPSC"
        description="Strategy decides, selection follows. CLARIAS is the intelligence layer behind UPSC preparation — showing why marks are lost, how toppers think, and what UPSC repeatedly rewards."
        canonical="/" ogType="website" />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org", "@type": "Organization",
          name: "CLARIAS", url: "https://clarias.in",
          description: "Decision Intelligence for UPSC — the intelligence layer behind UPSC preparation.",
          slogan: "Strategy Decides. Selection Follows.",
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org", "@type": "FAQPage",
          mainEntity: [
            { "@type": "Question", name: "Is CLARIAS another coaching platform?", acceptedAnswer: { "@type": "Answer", text: "No. CLARIAS is a decision-intelligence layer that shows you why marks are lost and what to do next, rather than serving more content." } },
            { "@type": "Question", name: "What is the Free Diagnostic?", acceptedAnswer: { "@type": "Answer", text: "A quick 5-minute assessment that returns your readiness score, weakest area and a tailored next step." } },
            { "@type": "Question", name: "Is there a free plan?", acceptedAnswer: { "@type": "Answer", text: "Yes — start free with the diagnostic and core intelligence, and upgrade only when the value is obvious." } },
          ],
        })}</script>
      </Helmet>

      {/* 1 · HERO */}
      <section style={{ backgroundColor: B1 }} className="relative overflow-hidden">
        <div className="absolute top-[-20%] left-[10%] w-[600px] h-[600px] rounded-full bg-[#f97316]/10 blur-[140px] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-8 pt-20 pb-24 md:pt-28 md:pb-32 grid lg:grid-cols-2 gap-14 items-center relative">
          <div>
            <Reveal>
              <span className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full border border-[rgba(255,255,255,0.12)] bg-white/[0.03] text-slate-300 mb-6">
                <Sparkles className="w-3.5 h-3.5 text-[#f97316]" /> Decision Intelligence for UPSC
              </span>
            </Reveal>
            <Reveal delay={0.05}>
              <h1 className={`${DISPLAY} text-5xl md:text-6xl font-semibold tracking-tighter leading-[1.05]`}>
                Strategy Decides.<br /><span className="text-[#f97316]">Selection Follows.</span>
              </h1>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-base md:text-lg text-slate-400 leading-relaxed max-w-xl">
                Most aspirants know <em className="text-slate-300 not-italic font-medium">what</em> to study. Very few understand why marks are lost, how successful candidates think, and what UPSC repeatedly rewards.
                <span className="block mt-3 text-slate-300">CLARIAS is the intelligence layer behind UPSC preparation.</span>
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-9 flex flex-col sm:flex-row gap-3">
                <button onClick={startDiagnostic} className={btnPrimary} data-testid="hero-diagnostic-btn">
                  Start Free Diagnostic <ArrowRight className="w-4 h-4" />
                </button>
                <button onClick={seeDemo} className={btnGhost} data-testid="hero-demo-btn">See Product Demo</button>
              </div>
              <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-xs text-slate-500">
                <span className="flex items-center gap-1.5"><CreditCard className="w-3.5 h-3.5 text-slate-600" /> No credit card</span>
                <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-slate-600" /> Under 5 minutes</span>
                <span className="flex items-center gap-1.5"><FileText className="w-3.5 h-3.5 text-slate-600" /> Instant readiness report</span>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <div className="relative">
              <div className="absolute -inset-4 bg-[#f97316]/5 blur-2xl rounded-3xl" />
              <div className="relative"><HeroDashboard /></div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 2 · WHY ASPIRANTS FAIL */}
      <Section id="why-fail" bg={B2}>
        <Reveal><div className="text-center max-w-2xl mx-auto mb-14">
          <Overline>The real problem</Overline>
          <h2 className={H2}>Why most aspirants fall short</h2>
        </div></Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {[[AlertTriangle, "Wrong Answer Structure"], [RotateCcw, "Poor Revision Decisions"], [Target, "Over-Attempting"], [History, "Weak PYQ Understanding"], [EyeOff, "No Performance Visibility"]].map(([Icon, t], i) => (
            <Reveal key={t} delay={i * 0.06}>
              <div className="rounded-2xl border border-[rgba(255,255,255,0.08)] bg-[#1A2233] p-5 h-full">
                <div className="w-10 h-10 rounded-xl bg-[#f97316]/12 flex items-center justify-center mb-4"><Icon className="w-5 h-5 text-[#f97316]" /></div>
                <p className="text-sm font-medium text-slate-200 leading-snug">{t}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}><p className="text-center mt-12 text-lg md:text-xl text-slate-300 max-w-2xl mx-auto">
          Preparation isn't only about studying harder. <span className="text-white font-medium">It's about making better decisions.</span>
        </p></Reveal>
      </Section>

      {/* 3 · WHY CURRENT SOLUTIONS AREN'T ENOUGH */}
      <Section id="gap" bg={B1}>
        <div className="max-w-3xl mx-auto text-center">
          <Reveal><Overline>The category gap</Overline></Reveal>
          <Reveal delay={0.05}><h2 className={H2}>More content was never the answer</h2></Reveal>
          <Reveal delay={0.1}>
            <div className="flex flex-wrap justify-center gap-3 mt-8 mb-8">
              {["More notes", "More videos", "More PDFs", "More tests"].map((t) => (
                <span key={t} className="text-sm px-4 py-2 rounded-lg border border-[rgba(255,255,255,0.08)] bg-[#1A2233] text-slate-400 line-through decoration-rose-500/60">{t}</span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.15}><p className="text-lg text-slate-400 leading-relaxed">
            None of them tell you <span className="text-white font-medium">why</span> you lose marks, <span className="text-white font-medium">what</span> to revise next, or <span className="text-white font-medium">where</span> your strategy is quietly failing. Volume isn't a decision engine.
          </p></Reveal>
          <Reveal delay={0.2}><p className="mt-8 text-xl text-[#f97316] font-medium">So what actually does?</p></Reveal>
        </div>
      </Section>

      {/* 4 · WHY CLARIAS EXISTS */}
      <Section id="mission" bg={B2}>
        <div className="max-w-3xl mx-auto text-center">
          <Reveal><Overline>Why we exist</Overline></Reveal>
          <Reveal delay={0.05}>
            <p className={`${DISPLAY} text-2xl md:text-4xl font-medium leading-snug tracking-tight text-white`}>
              Every year, thousands study the same syllabus. Yet only a few succeed. The difference is rarely effort —
              <span className="text-[#f97316]"> it's preparation decisions.</span>
            </p>
          </Reveal>
          <Reveal delay={0.1}><p className="mt-6 text-lg text-slate-400">CLARIAS was built to make those decisions visible.</p></Reveal>
        </div>
      </Section>

      {/* 4b · FREE DIAGNOSTIC / READINESS REPORT PREVIEW */}
      <Section id="report-preview" bg={B1}>
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div>
              <Overline>Start here · 5 minutes</Overline>
              <h2 className={H2}>Your free diagnostic → your readiness report</h2>
              <p className="mt-4 text-slate-400 text-lg leading-relaxed">
                Answer a short set of questions. In under five minutes you'll get a personalized readiness report — your score, your weakest area, and exactly what to fix next. No credit card. No commitment.
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                {["First-timers", "Repeat aspirants", "Working professionals", "Optional aspirants"].map((p) => (
                  <span key={p} className="text-xs px-3 py-1.5 rounded-full border border-[rgba(255,255,255,0.08)] bg-[#1A2233] text-slate-300 flex items-center gap-1.5">
                    <Users className="w-3 h-3 text-[#f97316]" /> {p}
                  </span>
                ))}
              </div>
              <button onClick={startDiagnostic} className={`${btnPrimary} mt-8`} data-testid="report-diagnostic-btn">
                Get my readiness report <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-2xl border border-[rgba(255,255,255,0.1)] bg-[#1A2233] p-6 shadow-2xl shadow-black/40">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2 text-slate-200 text-sm font-medium"><FileText className="w-4 h-4 text-[#f97316]" /> Readiness Report</div>
                <span className="text-[10px] text-slate-500">Sample</span>
              </div>
              <div className="flex items-center gap-5 mb-5">
                <Ring value={58} label="GS Score" />
                <div className="flex-1">
                  <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Verdict</p>
                  <p className="text-sm text-slate-200 leading-snug">Borderline — <span className="text-[#f97316]">Modern History</span> is pulling your score down.</p>
                </div>
              </div>
              <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-2">Subject-wise accuracy</p>
              <Bar label="Polity" value={72} tone="#22c55e" />
              <Bar label="Economy" value={55} tone="#f97316" />
              <Bar label="Modern History" value={31} tone="#ef4444" />
              <div className="mt-4 pt-4 border-t border-[rgba(255,255,255,0.08)] flex items-start gap-2">
                <Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                <p className="text-sm text-slate-200">Next step: strengthen Modern History basics before the next test.</p>
              </div>
            </div>
          </Reveal>
        </div>
      </Section>

      {/* 5 · THE INTELLIGENCE LAYER */}
      <Section id="intelligence" bg={B1}>
        <Reveal><div className="text-center max-w-2xl mx-auto mb-16">
          <Overline>The core</Overline>
          <h2 className={H2}>The intelligence engines behind CLARIAS</h2>
          <p className="mt-4 text-slate-400 text-lg">Most platforms provide content. <span className="text-white font-medium">CLARIAS provides intelligence.</span></p>
        </div></Reveal>
        <div className="grid lg:grid-cols-3 gap-6">
          {engines.map((e, i) => (
            <Reveal key={e.title} delay={i * 0.08}>
              <div className="rounded-2xl border border-[rgba(255,255,255,0.08)] bg-[#1A2233] p-6 h-full flex flex-col">
                <div className="w-11 h-11 rounded-xl bg-[#f97316]/12 flex items-center justify-center mb-5"><e.icon className="w-5 h-5 text-[#f97316]" /></div>
                <h3 className={`${DISPLAY} text-xl font-medium text-white mb-4`}>{e.title}</h3>
                <div className="flex items-center gap-2 text-xs mb-5">
                  <span className="px-2.5 py-1 rounded-md bg-white/[0.05] text-slate-300">{e.from}</span>
                  <ChevronRight className="w-4 h-4 text-[#f97316]" />
                  <span className="px-2.5 py-1 rounded-md bg-[#f97316]/15 text-[#f97316] font-medium">{e.to}</span>
                </div>
                <div className="flex flex-wrap gap-1.5 mb-5">
                  {e.tags.map((t) => <span key={t} className="text-[11px] px-2 py-1 rounded-md bg-white/[0.04] text-slate-400">{t}</span>)}
                </div>
                <p className="text-sm text-slate-400 leading-relaxed mb-4">{e.note}</p>
                <div className="mt-auto pt-4 border-t border-[rgba(255,255,255,0.08)] flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                  <p className="text-sm text-slate-200 font-medium">{e.outcome}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* 6 · PREPARATION JOURNEY */}
      <Section id="journey" bg={B2}>
        <Reveal><div className="text-center max-w-2xl mx-auto mb-14">
          <Overline>The path</Overline>
          <h2 className={H2}>From diagnostic to selection</h2>
        </div></Reveal>
        <div className="flex flex-wrap justify-center gap-3">
          {["Diagnostic", "Personal Strategy", "Foundation", "Current Affairs", "Practice", "Performance Tracking", "Weakness Detection", "Improvement", "Selection"].map((step, i, arr) => (
            <Reveal key={step} delay={i * 0.05} className="contents">
              <div className="flex items-center gap-3">
                <div className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border ${i === arr.length - 1 ? "border-[#f97316]/50 bg-[#f97316]/10" : "border-[rgba(255,255,255,0.08)] bg-[#1A2233]"}`}>
                  {i === arr.length - 1 && <Trophy className="w-4 h-4 text-[#f97316]" />}
                  <span className={`text-sm ${i === arr.length - 1 ? "text-[#f97316] font-semibold" : "text-slate-300"}`}>{step}</span>
                </div>
                {i < arr.length - 1 && <ArrowRight className="w-4 h-4 text-slate-600 hidden md:block" />}
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* 7 · SEE CLARIAS IN ACTION */}
      <Section id="see-in-action" bg={B1}>
        <Reveal><div className="text-center max-w-2xl mx-auto mb-16">
          <Overline>The product is the proof</Overline>
          <h2 className={H2}>See CLARIAS in action</h2>
          <p className="mt-4 text-slate-400 text-lg">Not slides. The actual intelligence surfaces you'll use daily.</p>
        </div></Reveal>
        <div className="space-y-5">
          {[
            { t: "Decision Dashboard", d: "Your readiness, marks lost and weak areas — in one glance.", panel: <DashPanel /> },
            { t: "Topper Answer Generator", d: "Selection-level answers, structured the way examiners reward.", panel: <AnswerPanel /> },
            { t: "PYQ Analyzer", d: "A decade of papers decoded into what actually repeats.", panel: <PYQPanel /> },
            { t: "Topic Heatmap", d: "Instantly see where UPSC concentrates its marks.", panel: <HeatmapPanel /> },
            { t: "Current Affairs Intelligence", d: "Only the events with real exam yield, tagged by paper.", panel: <CAPanel /> },
            { t: "Practice Hub", d: "Targeted practice on any chapter or subtopic that adapts to your weak areas.", panel: <PracticePanel /> },
            { t: "Performance Dashboard", d: "Track your trajectory, not just your scores.", panel: <PerfPanel /> },
          ].map((item, i) => (
            <Reveal key={item.t} delay={0.04}>
              <div className={`grid md:grid-cols-2 gap-6 items-center rounded-2xl border border-[rgba(255,255,255,0.08)] bg-[#111827] p-6 ${i % 2 ? "md:[&>*:first-child]:order-2" : ""}`}>
                <div>
                  <span className="text-[11px] font-mono text-[#f97316]">0{i + 1}</span>
                  <h3 className={`${DISPLAY} text-2xl font-medium text-white mt-1 mb-2`}>{item.t}</h3>
                  <p className="text-slate-400">{item.d}</p>
                </div>
                <div>{item.panel}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* 8 · COMPLETE UPSC ECOSYSTEM */}
      <Section id="ecosystem" bg={B2}>
        <Reveal><div className="text-center max-w-2xl mx-auto mb-14">
          <Overline>Everything, in one place</Overline>
          <h2 className={H2}>The complete UPSC ecosystem</h2>
        </div></Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {[
            [GraduationCap, "Foundation Hub", ["Structured static syllabus", "Deep Study chapters"]],
            [Newspaper, "Current Affairs Hub", ["Daily intelligence", "Mains-ready analysis"]],
            [Grid3x3, "Prelims Hub", ["MCQ practice", "PYQ analyzer", "Topic heatmaps"]],
            [PenTool, "Mains Hub", ["Answer generator", "GS1–GS4 engines"]],
            [Compass, "CSAT Hub", ["Aptitude solver", "Timed practice"]],
            [FileText, "Essay Hub", ["Essay engine", "Structure & value-add"]],
            [Scale, "Optionals Hub", ["Optional-specific tools", "Focused practice"]],
          ].map(([Icon, title, caps], i) => (
            <Reveal key={title} delay={i * 0.05}>
              <div className="rounded-2xl border border-[rgba(255,255,255,0.08)] bg-[#1A2233] p-6 h-full">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-[#f97316]/12 flex items-center justify-center"><Icon className="w-5 h-5 text-[#f97316]" /></div>
                  <h3 className="text-white font-semibold">{title}</h3>
                </div>
                <ul className="space-y-1.5">
                  {caps.map((c) => <li key={c} className="flex items-center gap-2 text-sm text-slate-400"><Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" /> {c}</li>)}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* 9 · INTELLIGENCE TOOLS */}
      <Section id="tools" bg={B1}>
        <Reveal><div className="text-center max-w-2xl mx-auto mb-14">
          <Overline>Only on CLARIAS</Overline>
          <h2 className={H2}>Intelligence tools others don't have</h2>
        </div></Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[[Layers, "PYQ Analyzer"], [Grid3x3, "Topic Heatmap"], [LineChart, "Strategy Analytics"], [Gauge, "Decision Dashboard"], [Activity, "Attempt Pattern Analysis"], [TrendingUp, "Performance Intelligence"]].map(([Icon, t], i) => (
            <Reveal key={t} delay={i * 0.05}>
              <div className="rounded-2xl border border-[rgba(255,255,255,0.08)] bg-[#111827] p-6 flex items-center gap-4 hover:border-[#f97316]/40 transition-colors">
                <div className="w-11 h-11 rounded-xl bg-[#f97316]/12 flex items-center justify-center shrink-0"><Icon className="w-5 h-5 text-[#f97316]" /></div>
                <span className="text-white font-medium">{t}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* 10 · TRUST */}
      <Section id="trust" bg={B2}>
        <Reveal><div className="text-center max-w-2xl mx-auto mb-14">
          <Overline>Built for serious aspirants</Overline>
          <h2 className={H2}>Why aspirants trust CLARIAS</h2>
        </div></Reveal>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            ["The feedback finally told me why I was losing marks, not just that I was.", "Repeat aspirant", "R"],
            ["The PYQ heatmap changed how I prioritised. I stopped over-studying low-yield topics.", "Working professional", "M"],
            ["It feels like a strategy layer on top of everything else I already do.", "First-timer", "A"],
          ].map(([q, role, initial], i) => (
            <Reveal key={i} delay={i * 0.07}>
              <div className="rounded-2xl border border-[rgba(255,255,255,0.08)] bg-[#1A2233] p-6 h-full">
                <p className="text-slate-200 leading-relaxed mb-6">"{q}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#f97316]/15 border border-[#f97316]/30 flex items-center justify-center text-[#f97316] font-semibold text-sm">{initial}</div>
                  <span className="text-sm text-slate-400">{role}</span>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="mt-10 overflow-hidden relative" data-testid="testimonial-marquee">
          <div className="flex gap-3 w-max animate-marquee">
            {[...Array(2)].flatMap((_, dup) => [
              "Finally understood WHY I lost marks.", "The heatmap fixed my prioritisation.",
              "Feels like a strategy layer on everything.", "Stopped over-studying low-yield topics.",
              "My answer structure improved in weeks.", "Revision decisions are no longer guesswork.",
            ].map((q, i) => (
              <span key={`${dup}-${i}`} className="shrink-0 text-sm text-slate-300 px-4 py-2.5 rounded-full border border-[rgba(255,255,255,0.08)] bg-[#1A2233] flex items-center gap-2">
                <Quote className="w-3.5 h-3.5 text-[#f97316]/60" /> {q}
              </span>
            )))}
          </div>
        </div>
      </Section>

      {/* 11 · PRICING */}
      <Section id="pricing" bg={B1}>
        <Reveal><div className="text-center max-w-2xl mx-auto mb-14">
          <Overline>Simple pricing</Overline>
          <h2 className={H2}>Coaching costs ₹50,000+</h2>
          <p className="mt-3 text-lg text-slate-400">CLARIAS costs less than one mistake.</p>
        </div></Reveal>
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto items-stretch">
          {/* FREE */}
          <Reveal>
            <div className="rounded-2xl border border-[rgba(255,255,255,0.08)] bg-[#111827] p-7 h-full flex flex-col">
              <h3 className={`${DISPLAY} text-xl font-semibold text-white`}>FREE</h3>
              <p className="text-slate-500 text-sm mt-1">Explore decision intelligence</p>
              <p className={`${DISPLAY} text-3xl font-semibold text-white mt-5`}>₹0</p>
              <ul className="space-y-2.5 mt-6 mb-7 flex-1">
                {["Free readiness diagnostic", "Sample PYQ analysis", "Limited practice", "Basic current affairs"].map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-slate-400"><Check className="w-4 h-4 text-slate-500 shrink-0" /> {f}</li>
                ))}
              </ul>
              <button onClick={startFree} className={btnGhost} data-testid="pricing-free-btn">Start Free</button>
            </div>
          </Reveal>
          {/* PRO */}
          <Reveal delay={0.06}>
            <div className="rounded-2xl border border-[#f97316] bg-gradient-to-b from-[#f97316]/[0.12] to-[#1A2233] p-7 h-full flex flex-col relative">
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-semibold px-3 py-1 rounded-full bg-[#f97316] text-white">RECOMMENDED</span>
              <h3 className={`${DISPLAY} text-xl font-semibold text-white`}>CLARIAS PRO</h3>
              <p className="text-slate-400 text-sm mt-1">Complete Prelims + Mains system</p>
              <div className="flex items-baseline gap-2 mt-5">
                <span className="text-slate-500 line-through text-sm">₹23,999</span>
                <span className={`${DISPLAY} text-3xl font-semibold text-white`}>₹6,999</span>
              </div>
              <p className="text-xs text-emerald-400 font-medium mt-1">Founding pricing • Save ₹17,000</p>
              <button onClick={() => navigate("/auth?upgrade=true")} className="mt-2 text-left text-xs text-slate-400 hover:text-[#f97316] transition-colors" data-testid="pricing-monthly-link">
                or start at <span className="text-white font-semibold">₹699/month</span> <span className="text-[#f97316]">→</span>
                <span className="block text-[10px] text-slate-500 mt-0.5">Monthly & Quarterly plans available</span>
              </button>
              <ul className="space-y-2.5 mt-5 mb-7 flex-1">
                {["Full Prelims Intelligence", "Complete CSAT Suite", "GS I–IV + Essay engines", "Smart Answer Evaluation", "Valid till Mains 2027 (2 attempts)"].map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-slate-300"><Check className="w-4 h-4 text-[#f97316] shrink-0" /> {f}</li>
                ))}
              </ul>
              <button onClick={() => navigate("/auth?upgrade=true")} className={btnPrimary} data-testid="pricing-pro-btn">Unlock PRO</button>
            </div>
          </Reveal>
          {/* OPTIONALS */}
          <Reveal delay={0.12}>
            <div className="rounded-2xl border border-[rgba(255,255,255,0.08)] bg-[#111827] p-7 h-full flex flex-col">
              <h3 className={`${DISPLAY} text-xl font-semibold text-white`}>Optionals</h3>
              <p className="text-slate-500 text-sm mt-1">Dedicated Paper I & II engines</p>
              <div className="flex items-baseline gap-2 flex-wrap mt-5">
                <span className="text-slate-500 line-through text-sm">₹5,999</span>
                <span className={`${DISPLAY} text-3xl font-semibold text-white`}>₹1,999</span>
                <span className="text-sm text-slate-500">each</span>
              </div>
              <ul className="space-y-2.5 mt-6 mb-7 flex-1">
                {["Sociology", "Anthropology", "Public Administration", "PSIR"].map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-slate-400"><Check className="w-4 h-4 text-purple-400 shrink-0" /> {f}</li>
                ))}
              </ul>
              <button onClick={startFree} className={btnGhost} data-testid="pricing-optionals-btn">View Optionals</button>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.15}><p className="text-center mt-10 text-slate-500 italic">No guarantees. No false promises.<br /><span className="text-slate-400">Just reduced failure probability.</span></p></Reveal>
      </Section>

      {/* 12 · FAQ */}
      <Section id="faq" bg={B2}>
        <Reveal><div className="text-center max-w-2xl mx-auto mb-12">
          <Overline>Questions</Overline>
          <h2 className={H2}>Frequently asked</h2>
        </div></Reveal>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible>
            {[
              ["Is CLARIAS another coaching platform?", "No. CLARIAS is a decision-intelligence layer that sits on top of your preparation — it shows you why marks are lost and what to do next, rather than just serving more content."],
              ["Do I need to leave my current coaching?", "Not at all. CLARIAS complements any coaching or self-study by adding strategy, diagnostics and pattern intelligence you don't get elsewhere."],
              ["What is the Free Diagnostic?", "A quick assessment that surfaces your readiness, likely marks lost and weak areas — so your very first session already gives you a decision, not just data."],
              ["Which papers does the answer engine support?", "GS1, GS2, GS3, GS4 and Essay — each with its own engine, because different papers reward different thinking."],
              ["How does PYQ Intelligence help?", "It reads a decade of past papers as signal — heatmaps, frequency and trends — so you study what UPSC repeatedly rewards."],
              ["Is my data private?", "Yes. Your performance data is used only to power your own insights and recommendations."],
              ["Is there a free plan?", "Yes — start free with the diagnostic and core intelligence, and upgrade only when the value is obvious."],
              ["Does it work on mobile?", "Fully. CLARIAS is built mobile-first so you can practise and review anywhere."],
            ].map(([q, a], i) => (
              <AccordionItem key={i} value={`f${i}`} className="border-[rgba(255,255,255,0.08)]">
                <AccordionTrigger data-testid={`faq-trigger-${i + 1}`} className="text-white hover:no-underline text-left py-5">{q}</AccordionTrigger>
                <AccordionContent className="text-slate-400 leading-relaxed">{a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </Section>

      {/* 13 · FINAL CTA */}
      <section style={{ backgroundColor: B1 }} className="relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-[500px] h-[500px] rounded-full bg-[#f97316]/15 blur-[130px]" />
        </div>
        <div className="max-w-3xl mx-auto px-6 py-28 md:py-36 text-center relative">
          <Reveal>
            <h2 className={`${DISPLAY} text-4xl md:text-6xl font-semibold tracking-tighter`}>Stop Guessing.<br /><span className="text-[#f97316]">Start Deciding.</span></h2>
          </Reveal>
          <Reveal delay={0.08}><p className="mt-6 text-lg text-slate-400">Every preparation decision has consequences. CLARIAS helps you identify them before the exam does.</p></Reveal>
          <Reveal delay={0.15}>
            <button onClick={startDiagnostic} className={`${btnPrimary} mt-10 px-8 py-4 text-base`} data-testid="final-diagnostic-btn">
              Start Free Diagnostic <ArrowRight className="w-5 h-5" />
            </button>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
